'use client';
import {
  Box,
  ButtonBase,
  ButtonProps,
  darken,
  getContrastRatio,
  lighten,
  useTheme,
  CircularProgress,
  Typography,
} from '@mui/material';
import Optional from '@jd/ui/components/Optional';

const buttonSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textTransform: 'uppercase',
  px: 3,
  py: 1.5,
  borderRadius: 1,
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
};

const AnimatedFillButton = ({ children, loading, ...rest }: ButtonProps) => {
  const theme = useTheme();
  const { loadingPosition, ...baseButtonProps } = rest;

  const contrastToDark = getContrastRatio(theme.palette.primary.main, '#000');
  const bgFn = contrastToDark > 2 ? darken : lighten;

  return (
    <ButtonBase
      {...baseButtonProps}
      disabled={loading || baseButtonProps.disabled}
      sx={{
        ...buttonSx,
        position: 'relative',
        overflow: 'hidden',
        transition: 'background-color 0.2s ease',
        ':hover': {
          textDecoration: 'none !important',
        },
        // The background fill pseudo-element
        '&::before': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: 0,
          backgroundColor: bgFn(theme.palette.primary.main, 0.15),
          transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 0,
        },
        ':hover::before': {
          height: loading ? 0 : '100%',
        },
        ...baseButtonProps.sx,
      }}
    >
      <Typography
        variant={'button'}
        sx={{
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.2s ease',
          fontSize: 15,
          lineHeight: 1.75,
          whiteSpace: 'nowrap',
          zIndex: 1,
        }}
      >
        {children}
      </Typography>

      <Optional condition={!!loading}>
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <CircularProgress size={24} sx={{ color: 'primary.contrastText' }} />
        </Box>
      </Optional>
    </ButtonBase>
  );
};

export default AnimatedFillButton;
